import * as client from "../../../client";
import { FinancialModelAuditSource } from "../layers/financialModelAuditTypes";

// Every cat vendor with its own results tab and run history. The union is
// derived from the array so iteration and exhaustive ``Record`` literals can't
// drift apart as vendors are added.
export const CAT_VENDORS = ["icm_ylt", "rms_elt", "spatialkat"] as const;

export type SelectedModel = (typeof CAT_VENDORS)[number];

// ICM YLT runs are stored under the "air" source.
export const selectedModelToAuditSource = (
  model: SelectedModel
): FinancialModelAuditSource => {
  switch (model) {
    case "rms_elt":
      return "rms";
    case "icm_ylt":
      return "air";
    case "spatialkat":
      return "spatialkat";
  }
};

export type ModelTabOption = SelectedModel | "rater" | "eqbd" | "sindri";

export type CatRunMode = client.CatRunSpec["mode"];

// The vendor data a run models with. Null is the broker SOV as-is.
export type EnrichmentSource = client.CatRunSpec["enrichment_source"];

// The same choice as a non-null key, for PrimeReact pickers: a null option
// value reads as "nothing selected" there, so broker data would render blank.
export type EnrichmentChoice = "broker" | NonNullable<EnrichmentSource>;

export const toEnrichmentChoice = (
  source: EnrichmentSource
): EnrichmentChoice => source ?? "broker";

export const fromEnrichmentChoice = (
  choice: EnrichmentChoice
): EnrichmentSource => (choice === "broker" ? null : choice);

export const ENRICHMENT_SOURCE_OPTIONS: {
  label: string;
  value: EnrichmentChoice;
}[] = [
  { label: "Broker data", value: "broker" },
  { label: "Hazard Hub & Nearmap", value: "nearmap" },
  { label: "Hazard Hub & Cape", value: "cape" },
];

export const enrichmentSourceLabel = (source: EnrichmentSource): string =>
  ENRICHMENT_SOURCE_OPTIONS.find((o) => o.value === toEnrichmentChoice(source))
    ?.label ?? "Broker data";

// One-word form for tight cells and pills.
export const enrichmentShortLabel = (source: EnrichmentSource): string =>
  source === "cape" ? "Cape" : source === "nearmap" ? "Nearmap" : "Broker";

// Every vendor whose live run is chosen per source on the Layers tab, with
// the name the tabs use for it.
export type EnrichmentVendor = client.VendorEnrichment["vendor"];

export const ENRICHMENT_VENDOR_LABELS: Record<EnrichmentVendor, string> = {
  icm_ylt: "ICM",
  rms_elt: "RMS",
  rms_22: "RMS 22",
  spatialkat: "KatRisk",
  rater: "Rater",
  eqbd_rater: "EB Rater",
};

// The "Enhanced · <roof source>" badge text for a run modeled with vendor data.
export const enhancedLabel = (source: string): string =>
  `Enhanced · ${source === "cape" ? "Cape" : "Nearmap"}`;

export type PerilCode =
  | "PWH"
  | "PSH"
  | "PPH"
  | "PES"
  | "PSL"
  | "PFF"
  | "PFL"
  | "PWB"
  | "PWW"
  | "PWT";

export const PERIL_OPTIONS: { label: string; value: PerilCode }[] = [
  { label: "Hurricane - Wind Only", value: "PWH" },
  { label: "Hurricane - Storm Surge", value: "PSH" },
  { label: "Earthquake - Ground Shake", value: "PES" },
  { label: "Earthquake - Sprinkler Leak.", value: "PSL" },
  { label: "Earthquake - Fire Following", value: "PFF" },
  { label: "Inland Flood", value: "PFL" },
  { label: "Wildfire", value: "PWB" },
  { label: "Winter Storm", value: "PWW" },
  { label: "Severe Thunderstorm", value: "PWT" },
];

export const PERIL_DISPLAY_NAMES = new Map<string, string>([
  ...PERIL_OPTIONS.map((o): [string, string] => [o.value, o.label]),
  // Asgard-derived combined perils; they show up in results but are not
  // requestable options (each runs whenever its wind-only / inland-flood-only
  // counterpart runs).
  ["PWH_PSH", "Hurricane - Wind and Surge"],
  ["PFL_PSH", "Flood - Inland and Surge"],
]);

// RMS EP Curve constants: we have more of this data, but keeping it limited
// for the time being since the table gets busy.
export const RETURN_PERIODS = [100, 250];

// Enriched RMS layer with matched layer_id from report config
export interface RmsLayerResult extends client.CatLayerResult {
  layer_id: string | null;
}

export interface LayerPerilRow {
  layer: string;
  peril: string;
  aal: number | null;
  pml_100: number | null;
  pml_250: number | null;
  pml_1000: number | null;
  // Portfolio Marginal Impact (ICM only), at 100% of each enabled layer —
  // gross basis matching the PML columns, pinned against participation
  // edits. Account-wide per peril — the same peril repeats its PMI across
  // layers; the Combined row shows no PMI (there is no all-peril figure).
  // Not additive across rows.
  pmi_100: number | null;
  pmi_250: number | null;
  pmi_1000: number | null;
  // AAL from each comparison run, keyed by batch id. Null when that run did
  // not model this layer or peril.
  aalByBatch: Record<string, number | null>;
  isFirstInGroup: boolean;
}

export const getPerilDisplayName = (perilCode: string): string => {
  const displayName = PERIL_DISPLAY_NAMES.get(perilCode) ?? perilCode;
  if (
    perilCode.toLowerCase() === "combined" ||
    displayName.toLowerCase() === "combined"
  ) {
    return "Layer Total";
  }
  return displayName;
};

export const isLayerTotalPeril = (perilCode: string): boolean =>
  getPerilDisplayName(perilCode).toLowerCase() === "layer total";
